/* eslint-disable @typescript-eslint/no-explicit-any */
import { assert } from "@animech/core-utilities";
import { Entity, Ray, Vec2, Vec3 } from "playcanvas";
import { isArrayProperty, vec3ToVector } from "@animech/axiom-utility";
import { nil } from "@animech/axiom-format";
import {
	AttacherInstance,
	AttacherSolver,
	AttachmentSolution,
	AttachmentSubsolution,
} from "@animech/supermodel";
import { DropOperation, DropOperationEvent } from "../../core/drag-and-drop";
import {
	createDefaultAttachmentSolution,
	detachEntityGroup,
	synchronizeAttachmentSolution,
} from "../attachers/utility";
import { CollisionSystem } from "../collision-system";
import { materialOverrides } from "../material-overrides";
import { AttacherState } from "../attachers/attacher-state";
import { PropertyWithVectors } from "./utility";
import {
	assertUnreachable,
	findParent,
	flatMapNodeHierarchy,
	isEntity,
	materialOverrideManager,
} from "src/utility";
import { OnAction, Property, SceneObject } from "src/common";
import { ConfigurationValue, ProductId } from "@definitions";

export type DroppableSceneObjectProperty = PropertyWithVectors<
	"position" | "rotation"
>;

export interface DropSceneObjectOperationArgs {
	object: SceneObject;
	property: DroppableSceneObjectProperty;
	collisionSystem: CollisionSystem;
	globalAttacherSolver: AttacherSolver;
	dropOperationInterface: DropSceneObjectOperationInterface;
	actionOnCancel: "revert-position" | "delete-property";
	positionObjectPivotAtPointer?: boolean;
}

export interface DropSceneObjectOperationInterface {
	getAttacherState(): AttacherState;
	getAttachedEntities(
		entityGroup: Entity[],
		includePeers: boolean,
		rank: "master" | "slave",
		maxSeparation: number | null,
		attacherInstancesOut?: AttacherInstance[],
	): Entity[];
	updateAttacherInstances(newAttacherInstances: AttacherInstance[]): void;
	syncEntityTransformsToAxiom(entities: Entity[]): void;

	getConfigurationValue<TValue>(
		value: ConfigurationValue<TValue>,
		archetype: Property,
	): TValue;

	getEntity(object: SceneObject): Entity | undefined;

	getSceneObject(entity: Entity): SceneObject | undefined;

	getCameraRay(x: number, y: number): Ray;
	getScreenPosition(worldPosition: Vec3): Vec2;
	transformScreenToWorld(x: number, y: number): Vec3 | undefined;

	onAction: OnAction;
	select(...entities: Entity[]): void;
	instantiateProduct(
		productId: ProductId,
		disableHistory?: boolean,
	): SceneObject;
	toggleDragOperationActive(active: boolean): void;

	pushAutoRenderRequest(): void;
	popAutoRenderRequest(): void;
	pushSingleFrameRequest(): void;
}

interface EntityAttachmentData {
	ignoredEntitiesForRaycast: Entity[];
	entityGroup: Entity[];
	groupSolution: AttachmentSolution;
	groupOffsets: Vec3[];
}

interface DropSceneObjectOperationState {
	entity: Entity;
	entityAttachmentData: EntityAttachmentData;
	initial: {
		entityPosition: Vec3;
		pointerEntityScreenOffset: Vec2;
		attacherInstances: AttacherInstance[];
	};
	foundSubsolution?: AttachmentSubsolution;
	isColliding?: boolean;
}

export class DropSceneObjectOperation implements DropOperation {
	private _args: DropSceneObjectOperationArgs;
	private _state?: DropSceneObjectOperationState;

	constructor(args: DropSceneObjectOperationArgs) {
		this._args = { ...args };
	}

	onEvent(event: DropOperationEvent): boolean {
		const type = event.type;
		switch (type) {
			case "dragEnter":
				return this._onDragEnter({ x: event.x, y: event.y });
			case "dragOver":
				return this._onDragOver({ x: event.x, y: event.y });
			case "dragLeave":
				return this._onDragLeave();
			case "drop":
				return this._onDrop();
			default:
				assertUnreachable(type);
		}
	}

	private _onDragEnter(event: { x: number; y: number }) {
		assert(
			!this._state,
			"Received dragEnter event when operation is already running",
		);

		const { object, dropOperationInterface } = this._args;
		const {
			getEntity,
			getAttacherState,
			pushAutoRenderRequest,
			toggleDragOperationActive,
			getScreenPosition,
		} = dropOperationInterface;

		const entity = getEntity(object);
		assert(entity);
		const entityPosition = entity.getPosition().clone();
		const entityAttachmentData = this._createEntityAttachmentData(entity);

		const pointerScreenPosition = new Vec2(event.x, event.y);
		const pointerPosition = this._convertScreenToWorld(
			pointerScreenPosition,
			entityAttachmentData,
		);
		if (!pointerPosition) {
			return true;
		}

		const attacherState = getAttacherState();
		const entityScreenPosition = getScreenPosition(entityPosition);
		const pointerEntityScreenOffset = entityScreenPosition
			.clone()
			.sub(new Vec2(event.x, event.y));

		this._state = {
			entity,
			initial: {
				entityPosition,
				pointerEntityScreenOffset,
				attacherInstances: AttacherSolver.copyInstances(attacherState.getAll()),
			},
			entityAttachmentData,
		};

		pushAutoRenderRequest();
		toggleDragOperationActive(true);

		return false;
	}

	private _onDragOver(event: { x: number; y: number }) {
		assert(
			!!this._state,
			"Received dragOver event before the operation was started",
		);

		const { positionObjectPivotAtPointer } = this._args;
		const { entity, entityAttachmentData, initial } = this._state;
		const pointerScreenPosition = new Vec2(event.x, event.y);
		const pointerEntityScreenOffset = positionObjectPivotAtPointer
			? Vec2.ZERO
			: initial.pointerEntityScreenOffset;
		const pointerPosition = this._convertScreenToWorld(
			pointerScreenPosition,
			entityAttachmentData,
			pointerEntityScreenOffset,
		);
		if (!pointerPosition) {
			return false;
		}

		entity.setPosition(pointerPosition);
		this._resolveEntityPlaneCollisions(entity);

		this._updateAttachmentSolution();
		this._updateCollisionState();
		this._updatePlacementState();

		return false;
	}

	private _onDragLeave() {
		assert(
			!!this._state,
			"Received dragLeave event before the operation was started",
		);

		const {
			initial,
			entityAttachmentData: { entityGroup, groupOffsets },
		} = this._state;
		const { property, actionOnCancel, dropOperationInterface } = this._args;
		const {
			onAction,
			updateAttacherInstances,
			popAutoRenderRequest,
			pushSingleFrameRequest,
			toggleDragOperationActive,
		} = dropOperationInterface;

		if (actionOnCancel === "delete-property") {
			const parent = property.getParent();
			if (parent && isArrayProperty(parent)) {
				onAction({
					action: {
						type: "setProperty",
						property: property as any,
						value: nil,
					},
					archetype: property,
				});
			}
		} else if (actionOnCancel === "revert-position") {
			// No "product still current" guard is needed here: this revert writes no
			// configuration, and it runs on live entities, because deleting a product
			// invalidates its drag inside the build.
			updateAttacherInstances(initial.attacherInstances);

			for (const [entityIndex, entity] of entityGroup.entries()) {
				entity.setPosition(
					initial.entityPosition.clone().add(groupOffsets[entityIndex]),
				);
			}

			// Ensures visualized collision- and placement states
			// are correct for reverted attacher state.
			this._updateCollisionState();
			this._updatePlacementState(true);
		}

		this._state = undefined;
		popAutoRenderRequest();
		pushSingleFrameRequest();
		toggleDragOperationActive(false);
		return true;
	}

	private _onDrop() {
		assert(
			!!this._state,
			"Received drop event before the operation was started",
		);

		const { property, dropOperationInterface } = this._args;
		const { entity, entityAttachmentData, isColliding, foundSubsolution } =
			this._state;
		const {
			onAction,
			select,
			syncEntityTransformsToAxiom,
			popAutoRenderRequest,
			toggleDragOperationActive,
		} = dropOperationInterface;

		if (isColliding || !foundSubsolution) {
			// We don't allow the user to "commit" the drop operation when some of
			// the dragged entities are colliding with other entities, or when
			// no valid attachment solution has been found.
			// Instead we'll just cancel the operation.
			return this._onDragLeave();
		}

		syncEntityTransformsToAxiom(entityAttachmentData.entityGroup);

		onAction({
			action: {
				type: "list",
				subactions: [
					{
						type: "setProperty",
						property: property.position as any,
						value: vec3ToVector(entity.getLocalPosition()) as any,
					},
					{
						type: "setProperty",
						property: property.rotation as any,
						value: vec3ToVector(entity.getLocalEulerAngles()) as any,
					},
				],
			},
			archetype: property,
		});
		select(entity);

		this._state = undefined;
		popAutoRenderRequest();
		toggleDragOperationActive(false);
		return true;
	}

	private _resolveEntityPlaneCollisions(entity: Entity) {
		assert(this._state);
		const { collisionSystem } = this._args;

		collisionSystem.step();

		const offset = new Vec3();
		const maxOffset = new Vec3();
		const contacts = collisionSystem.getEntityBodyContacts(entity);

		for (const contact of contacts) {
			if (contact.otherColliderMeta.definitionType !== "plane") {
				continue;
			}
			offset.copy(contact.otherNormal).mulScalar(-contact.distance);
			for (const key of ["x", "y", "z"] as const) {
				const current = offset[key];
				if (Math.abs(current) > Math.abs(maxOffset[key])) {
					maxOffset[key] = current;
				}
			}
		}

		if (maxOffset.lengthSq() > 0) {
			entity.translate(maxOffset);
			collisionSystem.step();
		}
	}

	private _convertScreenToWorld(
		pointerScreenPosition: Vec2,
		entityAttachmentData: EntityAttachmentData,
		pointerEntityScreenOffset = Vec2.ZERO,
	) {
		const { x, y } = pointerScreenPosition.add(pointerEntityScreenOffset);
		const { ignoredEntitiesForRaycast } = entityAttachmentData;
		const { dropOperationInterface, collisionSystem } = this._args;
		const { getCameraRay } = dropOperationInterface;
		const cameraRay = getCameraRay(x, y);
		const rayCastHit = collisionSystem.rayCast(cameraRay, {
			ignoredEntities: ignoredEntitiesForRaycast,
		});

		return rayCastHit?.position;
	}

	private _createEntityAttachmentData(entity: Entity): EntityAttachmentData {
		const { dropOperationInterface } = this._args;
		const { getAttachedEntities, getSceneObject } = dropOperationInterface;

		const attachedEntities = getAttachedEntities(
			[entity],
			false,
			"slave",
			null,
		);
		const entityGroup = [entity, ...attachedEntities];

		const isController = (entity: Entity) =>
			getSceneObject(entity)?.type === "controller";

		const ignoredEntitiesForRaycast = flatMapNodeHierarchy(
			entityGroup,
			(node): node is Entity =>
				isEntity(node) && !findParent(node, isController),
		);

		return {
			ignoredEntitiesForRaycast,
			entityGroup,
			groupSolution: createDefaultAttachmentSolution(entityGroup),
			groupOffsets: entityGroup.map((entity) =>
				new Vec3().sub2(entity.getPosition(), entityGroup[0].getPosition()),
			),
		};
	}

	private _updateAttachmentSolution() {
		assert(this._state);
		const { entityAttachmentData } = this._state;
		const { dropOperationInterface, globalAttacherSolver } = this._args;
		const { getAttacherState, updateAttacherInstances } =
			dropOperationInterface;
		const attacherState = getAttacherState();

		const { entityGroup, groupSolution, groupOffsets } = entityAttachmentData;
		assert(entityGroup.length > 0);

		for (const [entityIndex, entity] of entityGroup.entries()) {
			entity.setPosition(
				entityGroup[0].getPosition().clone().add(groupOffsets[entityIndex]),
			);
		}

		synchronizeAttachmentSolution(groupSolution, attacherState);
		// supermodel 10 removed `synchronizeTransforms`; `updateInstances` with
		// `skipAttachment` reproduces its transforms-only behaviour (attachment data
		// is recomputed separately below via detachEntityGroup).
		globalAttacherSolver.updateInstances(attacherState.getAll(), {
			skipAttachment: () => true,
		});

		const frozenInstances: AttacherInstance[] = [];
		const activeInstances: AttacherInstance[] = [];

		detachEntityGroup(
			entityGroup,
			attacherState.getAll(),
			activeInstances,
			frozenInstances,
		);

		const subsolutions = globalAttacherSolver.findGroupSubsolutions(
			frozenInstances,
			groupSolution,
		);
		let foundSubsolution: AttachmentSubsolution | undefined;

		if (subsolutions.length > 0) {
			globalAttacherSolver.sortSubsolutions(subsolutions, entityGroup);
			foundSubsolution = subsolutions[0];

			globalAttacherSolver.applySolution(foundSubsolution);
			updateAttacherInstances([
				...foundSubsolution.frozenInstances,
				...foundSubsolution.activeInstances,
			]);
		} else {
			updateAttacherInstances([...frozenInstances, ...activeInstances]);
		}

		this._state.foundSubsolution = foundSubsolution;
	}

	private _updateCollisionState() {
		assert(this._state);
		const { collisionSystem } = this._args;
		const { entityAttachmentData } = this._state;
		const { entityGroup } = entityAttachmentData;

		collisionSystem.step();

		this._state.isColliding = entityGroup.some((entity) =>
			collisionSystem.collidingEntities.has(entity),
		);
	}

	private _updatePlacementState(reset?: boolean) {
		assert(this._state);
		const { entityAttachmentData, foundSubsolution } = this._state;
		const { ignoredEntitiesForRaycast } = entityAttachmentData;

		const useOverride = !foundSubsolution && !reset;
		materialOverrideManager.toggle(
			useOverride,
			materialOverrides.invalidPlacement,
			ignoredEntitiesForRaycast,
		);
	}
}
